import { ensureArray } from '@agilepoint/utils-array';
import styled from 'styled-components';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { ITreeViewProps, ITreeItem } from './tree-view-types';
import { TreeItem, SearchField, ModeIndicator } from './parts';
import { useHierarchicalData } from '../../hooks';
import { FlexBox } from '../flex-box';

const Wrapper = styled(FlexBox).attrs({ className: String('ap-treeView-wrapper') }) <{ $isInCompactMode: boolean; }>`
    width: ${({ $isInCompactMode }) => $isInCompactMode && 'auto !important'};
`;

const ItemsWrapper = styled.div.attrs({
    className: String('ap-treeView-itemsWrapper')
})`
    overflow: auto;
    max-height: 100%;
    box-sizing: border-box;
    padding: ${({ theme: { semantics: { gap } } }) => gap.l};
`;

const TreeViewBase = memo((props: ITreeViewProps) => {
    const {
        className, elementRef, items, onRenderItem, triggerChangeOnParentSelection, showSearchBox, hideCollapsibleButton,
        onChange, defaultSelectedId, selectedId: selectedIdFromProps, defaultMode = 'default', onModeChange,
        disableModeChangeOnHover, loadOnDemand, onFetchChildren, defaultExpandedId, searchPlaceholder,
        enableAccordionMode, searchPredicate, retainHierarchyInSearchResult, onRenderContent
    } = props;

    const [mode, setMode] = useState(defaultMode);
    const [isMenuCollapsed, setMenuState] = useState(defaultMode === 'compact');
    const [searchText, setSearchText] = useState('');

    const _onToggleViewModeClick = useCallback(() => {
        setMode(state => {
            const inCompactMode = state === 'compact';
            setMenuState(!inCompactMode);
            const newState = inCompactMode ? 'default' : 'compact';

            if (onModeChange) {
                onModeChange(newState);
            }

            return newState;
        });
    }, [onModeChange]);

    const {
        isExpanded, shouldHighlightItem, getChildItemsByParentId,
        hasChildren, setSelectedId, toggleExpandedState
    } = useHierarchicalData({ defaultSelectedId, items, searchText: isMenuCollapsed ? '' : searchText, selectedId: selectedIdFromProps, isAccordionModeEnabled: enableAccordionMode, retainHierarchyInSearchResult, searchPredicate });

    const _onItemClick = useCallback(async (id: React.Key) => {
        if (onChange !== undefined) {
            onChange(id);
        }
        setSelectedId(id);
    }, [onChange, setSelectedId]);

    const _toggleExpandedState = useCallback(async (id: React.Key, shouldFetchChildren: boolean) => {
        let result: boolean | undefined;
        if (shouldFetchChildren && onFetchChildren) {
            result = await onFetchChildren(id);
        }
        if (result === false) {
            return;
        }
        toggleExpandedState(id);
    }, [onFetchChildren, toggleExpandedState]);

    const defaultExpandedIdRef = useRef(defaultExpandedId ? ensureArray(defaultExpandedId) : []);

    const onDefaultExpanded = useCallback((id: React.Key) => {
        defaultExpandedIdRef.current = defaultExpandedIdRef.current.filter(i => i !== id);
    }, []);

    const _renderItem = useCallback((item: ITreeItem) => (
        <TreeItem
            item={item}
            key={item.id}
            defaultExpanded={defaultExpandedIdRef.current.includes(item.id)}
            loadOnDemand={loadOnDemand}
            isInCompactMode={isMenuCollapsed}
            hasChildren={isMenuCollapsed ? false : hasChildren(item.id)}
            isExpanded={isExpanded(item.id)}
            shouldHighlightItem={shouldHighlightItem(item.id)}
            triggerChangeOnParentSelection={triggerChangeOnParentSelection}
            getChildItems={getChildItemsByParentId}
            renderChildren={_renderItem}
            onClick={_onItemClick}
            onExpandToggle={_toggleExpandedState}
            onRenderItem={onRenderItem}
            onRenderContent={onRenderContent}
            onDefaultExpanded={onDefaultExpanded} />
    ), [loadOnDemand, isMenuCollapsed, hasChildren, isExpanded, shouldHighlightItem, triggerChangeOnParentSelection, getChildItemsByParentId, _onItemClick, _toggleExpandedState, onRenderItem, onRenderContent, onDefaultExpanded]);

    const isInCompactMode = mode === 'compact';
    const _onMouseEnter = useCallback(() => {
        if (!isInCompactMode || disableModeChangeOnHover) {
            return;
        }
        setMenuState(false);
    }, [disableModeChangeOnHover, isInCompactMode]);

    const _onMouseLeave = useCallback(() => {
        if (!isInCompactMode || disableModeChangeOnHover) {
            return;
        }
        setMenuState(true);
    }, [disableModeChangeOnHover, isInCompactMode]);

    const rootItems = useMemo(() => getChildItemsByParentId(), [getChildItemsByParentId]);

    return (
        <Wrapper
            className={className}
            ref={elementRef}
            flexDirection="column"
            inline
            onMouseEnter={_onMouseEnter}
            onMouseLeave={_onMouseLeave}
            $isInCompactMode={isMenuCollapsed}>
            {showSearchBox && <SearchField defaultValue={searchText} searchPlaceholder={searchPlaceholder} isCollapsed={isMenuCollapsed} onChange={setSearchText} />}
            <ItemsWrapper>
                {rootItems.map(_renderItem)}
            </ItemsWrapper>
            <ModeIndicator hidden={hideCollapsibleButton} isInCompactMode={isInCompactMode} onClick={_onToggleViewModeClick} />
        </Wrapper>
    );
});

TreeViewBase.displayName = 'TreeView';

export const TreeView = styled(TreeViewBase)`
    border-style: solid;
    box-sizing: border-box;
    user-select: none;
    position: relative;
    background-color: ${({ theme: { treeView: { colors } } }) => colors.backgroundColor};
    border-color: ${({ theme: { treeView: { colors } } }) => colors.borderColor};
    border-width: ${({ theme: { treeView: { composites } } }) => composites.borderWidth.root};
    border-radius: ${({ theme: { treeView: { composites } } }) => composites.borderRadius.root};
`;

TreeView.displayName = 'TreeView';
